<template>
  <div>
    <div
      v-if="showHeader"
      class="title-container"
      :class="{ 'non-collapsable': !isCollapsable }"
    >
      <BaseButton
        v-if="isCollapsable"
        kind="auxiliary"
        type="secondary"
        class="collapse-button"
        @click="toggleCollapse"
      >
        <template #title>
          {{ title }}
          <span
            v-if="items.length > 0"
            class="task-count"
            :class="{ 'task-count--overdue': isOverdue }"
            >{{ items.length }}</span
          >
        </template>
        <template #icon>
          <ChevronRightIcon :class="{ expanded: !isCollapsed }" />
        </template>
      </BaseButton>
      <div v-else class="title">
        {{ title }}
        <span
          v-if="items.length > 0"
          class="task-count"
          :class="{ 'task-count--overdue': isOverdue }"
          >{{ items.length }}</span
        >
      </div>
    </div>

    <template v-if="!showHeader || !isCollapsed || !isCollapsable">
      <TaskList
        ref="taskListRef"
        :can-add-task="canAddTask"
        :add-task-action="addTaskAction"
        :pagination="pagination"
        :date="date"
        :empty-text="emptyText"
        :items="items"
        :is-today="isToday"
        :hide-scheduled-date="hideScheduledDate"
        :list-key="listKey"
        :allow-reorder="allowReorder"
        @unselect-items="$emit('unselectItems')"
        @is-active="$emit('isActive', $event)"
      />
    </template>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType, ref } from "vue";

import type {
  ConsoleActionTask,
  GeneralizedTaskId,
} from "@ctrl/controller-rpc";

import TaskList from "@/components/agenda/TaskList.vue";
import BaseButton from "@/components/base/BaseButton.vue";
import ChevronRightIcon from "@/components/icons/ChevronRightIcon.vue";

export default defineComponent({
  name: "CollapsibleTaskList",
  components: {
    BaseButton,
    ChevronRightIcon,
    TaskList,
  },
  props: {
    canAddTask: { type: Boolean, default: false },
    /** How the "Add task…" row schedules the created task: see TaskList. */
    addTaskAction: {
      type: Object as PropType<ConsoleActionTask>,
      required: false,
      default: undefined,
    },
    date: { type: Date, required: true },
    emptyText: { type: String, required: false },
    items: { type: Array as PropType<GeneralizedTaskId[]>, required: true },
    isToday: { type: Boolean, required: true },
    title: { type: String, required: true },
    /**
     * Without a header the list is bare (and always shown): no title row,
     * nothing to collapse — e.g. a canvas Tasks widget with a single section.
     */
    showHeader: { type: Boolean, default: true },
    isCollapsed: { type: Boolean, default: true },
    isCollapsable: { type: Boolean, default: true },
    isOverdue: { type: Boolean, default: false },
    pagination: { type: Number, required: false, default: undefined },
    hideScheduledDate: { type: Boolean, default: false },
    listKey: { type: String, required: false, default: undefined },
    allowReorder: { type: Boolean, default: true },
  },
  emits: {
    unselectItems: () => true,
    isActive: (_: boolean) => true,
    "update:isCollapsed": (_: boolean) => true,
  },
  setup(props, { emit }) {
    // Reference to the child TaskList component
    const taskListRef = ref<InstanceType<typeof TaskList>>();

    // Expose methods that the parent component expects
    const unselectTask = () => {
      taskListRef.value?.unselectTask();
    };
    const focusAddTask = () => {
      taskListRef.value?.focusAddTask?.();
    };

    const toggleCollapse = () => {
      unselectTask();
      emit("update:isCollapsed", !props.isCollapsed);
    };

    return {
      taskListRef,
      focusAddTask,
      unselectTask,
      toggleCollapse,
    };
  },
});
</script>

<style scoped lang="scss">
.title-container {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 4px;
  padding-block: 4px;
  // Align with TaskInline checkboxes (handle column 8+16+8 = 32px).
  margin-inline-start: 0;
  padding-inline-start: 32px;

  // Collapse control adds 4px pad — pull back so the label still matches.
  &:has(.collapse-button) {
    padding-inline-start: calc(32px - 4px);
  }

  &.non-collapsable {
    .title {
      margin-bottom: 4px;
    }
  }
}

.title {
  @extend %font-h4;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-inline-end: 4px;
}

.task-count {
  font-weight: 300;
  @include themed() {
    color: t($grey-50);
  }

  &--overdue {
    padding: 1px 6px;
    margin-inline-start: -2px;
    border-radius: $border-radius-m;
    @include themed() {
      background-color: t($secondary-10);
      color: t($secondary-50);
    }
  }
}

:deep(.collapse-button) {
  padding: 4px;

  .title {
    @extend .title;
  }

  .icon {
    svg {
      transition: transform 0.2s ease;

      &.expanded {
        transform: rotate(90deg);
      }
    }
  }
}

.loading-container {
  align-items: center;
  padding: 20px;
  padding-inline-start: 32px;
}
</style>
